import { Lock, Wrench } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  getClientIdentity,
  selectMaintenanceActive,
  useMaintenanceLeaveMutation,
  useMaintenanceReportMutation,
  type MaintenanceWorkplaceState,
} from '@krp/client-runtime'
import { SaleActivityContext, type SaleActivity } from '@krp/sale-ui'

import './maintenanceGate.css'
import { useAppSelector } from './hooks'

/** Текст по умолчанию, если администратор не задал свой (ADR-8: заданный показывается дословно). */
const DEFAULT_LOCK_TEXT = 'Идёт обновление системы. Касса временно недоступна.'

/** Пульс рабочего места в пульт обновления: сервер считает молчащее дольше 40 с местом «нет связи». */
export const WORKPLACE_HEARTBEAT_MS = 15_000

/**
 * Замок режима обновления РМК (RP-MNT) вокруг экрана продажи. Пока режим включён:
 * - идёт оплата или набрана корзина — не прерываем, даём завершить; сверху плашка, новые продажи закрыты;
 * - корзина пуста — показываем замок вместо рабочего места: начать новую продажу или смену нельзя.
 * Замок закрывает именно продажу, а не всё приложение. Чем занята касса, говорит сам экран продажи (`SaleActivityContext`); то же
 * состояние (свободна, корзина, оплата, заблокирована) рабочее место сообщает пульту обновления — сразу при смене и пульсом.
 * Когда режим снят (сервер вернулся), замок исчезает сам; новую сборку клиента подхватывает плашка «Вышла новая версия».
 */
export function MaintenanceGate({ resourceId, label, children }: { resourceId: string; label: string; children: ReactNode }) {
  const active = useAppSelector((state) => selectMaintenanceActive(state.maintenance))
  const message = useAppSelector((state) => state.maintenance.message)
  const [activity, setActivity] = useState<SaleActivity>('idle')
  const state: MaintenanceWorkplaceState = active && activity === 'idle' ? 'locked' : activity
  const [report] = useMaintenanceReportMutation()
  const [leave] = useMaintenanceLeaveMutation()
  const [workplaceId] = useState(() => getClientIdentity().tabId())

  useEffect(() => {
    const send = () => void report({ workplaceId, resourceId: resourceId === '' ? null : resourceId, label, state })
    send()
    const timer = window.setInterval(send, WORKPLACE_HEARTBEAT_MS)
    return () => window.clearInterval(timer)
  }, [label, report, resourceId, state, workplaceId])
  // Окно РМК ушло с экрана — пульт убирает его сразу, а не по молчанию.
  useEffect(() => () => void leave({ workplaceId }), [leave, workplaceId])

  if (state === 'locked') {
    return (
      <div className="maintenance-lock" role="alert" aria-live="assertive">
        <div className="maintenance-lock__panel">
          <Lock size={40} aria-hidden="true" />
          <h2 className="maintenance-lock__title">Касса закрыта на время обновления</h2>
          <p className="maintenance-lock__text">{message ?? DEFAULT_LOCK_TEXT}</p>
          <p className="maintenance-lock__hint">Когда обновление закончится, касса откроется сама. Если потребуется — обновите страницу.</p>
        </div>
      </div>
    )
  }

  return (
    <SaleActivityContext.Provider value={setActivity}>
      {active && (
        <div className="maintenance-banner" role="status" aria-live="polite">
          <Wrench size={16} aria-hidden="true" />
          <span>
            Скоро обновление. Завершите текущий чек — новые продажи закрыты.
            {message !== null ? ` ${message}` : ''}
          </span>
        </div>
      )}
      {children}
    </SaleActivityContext.Provider>
  )
}
